'use client';

import { useContext, useMemo, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import type { SearchListingsRequest } from '@/src/lib/api';
import type { HpBoundingBoxDto, HpSearchScopeDto } from '@zg-rentals/ts-hotpads-api-client';
import { LISTINGS_PER_PAGE } from '@/src/lib/constants';
import { FilterContext } from '@/src/lib/filters/FilterProvider';
import { deserializeFromUrl } from '@/src/lib/filters/filterSerializer';
import { isLiveMapPosition, useMapBorderState, useMapPosition } from '@/src/lib/hooks';
import { getSearchScopeKey } from '@/src/lib/filters/searchScope';
import { buildSrpFilters } from '../_data/searchRequest';
import type { MapState } from '@/src/lib/hooks/map/mapStateKeys';
import { normalizeBoundsForListingQuery } from '@/src/lib/map/viewportBounds';
import { getSrpPageFromUrl } from '@/src/lib/seo/srp-pagination';
import { useAllRentalsSlug } from '@/src/lib/seo/AllRentalsSlugProvider';

type SearchBoundingBox = HpBoundingBoxDto;
type HotpadsWebListingFilter = NonNullable<SearchListingsRequest['filters']>;

/**
 * Builds the live V3 `SearchListingsRequest` shared by `LiveListingsGrid` and
 * `CountsLine`. Starts from the SSR `initialParams` (which carries
 * `searchSlug` + `area`) and overrides `filters.boundingBox` from the live map
 * viewport, the filters from `FilterContext` (the BE applies the slug's
 * defaults, so they aren't re-sent), and the page from the URL.
 *
 * Returns the SAME `initialParams` reference when nothing has changed, so the
 * caller can detect the SSR-seeded case (`liveParams === initialParams`) and
 * avoid a duplicate fetch on mount.
 */
export function useSrpLiveSearchRequest({
  initialParams,
  scope,
  page,
}: {
  initialParams: SearchListingsRequest;
  scope?: HpSearchScopeDto;
  page: number;
}): SearchListingsRequest {
  const areaKey = getSearchScopeKey(scope);
  const mapPosition = useMapPosition();
  const borderState = useMapBorderState();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const filterContext = useContext(FilterContext);
  const filters = filterContext?.filters;
  const hasInteracted = filterContext?.hasInteracted ?? false;
  const allRentalsSlug = useAllRentalsSlug();
  // The URL's query-only filters (what SSR sent for this slug). Used before any
  // interaction so the live request matches the SSR fetch and doesn't refetch.
  const queryFilters = useMemo(() => {
    const record: Record<string, string> = {};
    searchParams?.forEach((value, key) => {
      record[key] = value;
    });
    return deserializeFromUrl(record);
  }, [searchParams]);
  const currentPage = getSrpPageFromUrl(pathname, searchParams?.get('page') ?? undefined);
  const areaMapPosition = mapPosition?.areaKey === areaKey ? mapPosition : undefined;
  const initialAreaMapPositionRef = useRef<{
    areaKey: string | undefined;
    initialParams: SearchListingsRequest;
    value: MapState | undefined;
  }>();

  if (
    !initialAreaMapPositionRef.current ||
    initialAreaMapPositionRef.current.areaKey !== areaKey ||
    initialAreaMapPositionRef.current.initialParams !== initialParams
  ) {
    // A map-sourced position found at mount is a viewport the user panned to
    // (e.g. while an HDP was open over this SRP) — don't adopt it as the
    // baseline, so the live request rebuilds from it. Cache-bridge seeds
    // ('boundary'/'url'/'listing'/'restore') still count as the initial
    // position so an untouched mount reuses the SSR request.
    initialAreaMapPositionRef.current = {
      areaKey,
      initialParams,
      value: isLiveMapPosition(areaMapPosition) ? undefined : areaMapPosition,
    };
  }

  return useMemo(() => {
    const initialBox = (initialParams.filters?.boundingBox ?? undefined) as SearchBoundingBox | undefined;
    // "Area Border" on → clip to the scope; off → raw viewport.
    // Before MapStateHydrator writes the area-scoped border cache, fall back to
    // the server request so `?border=false` does not briefly re-apply scope.
    const initialShowBorder = initialParams.filters?.scope != null;
    const borderIsForArea = areaKey != null && borderState.boundaryAreaId === areaKey;
    const showBorder = borderIsForArea ? borderState.showBorder : initialShowBorder;
    const clipScope = showBorder && initialParams.area?.type !== 'university' ? scope : undefined;

    // Pre-interaction: mirror SSR (current slug + the URL's query-only filters).
    // Post-interaction: the all-rentals slug (no type filter) + the full filter set, for promote/demote.
    const effectiveFilterState = hasInteracted ? filters : queryFilters;
    const anchorSlug = hasInteracted ? allRentalsSlug : initialParams.searchSlug;

    const filtersChangedFromInitial =
      !!effectiveFilterState &&
      JSON.stringify(buildSrpFilters(effectiveFilterState, initialBox, clipScope)) !==
        JSON.stringify(initialParams.filters ?? {});
    let boundingBox: SearchBoundingBox | undefined = initialBox;
    const shouldUseLiveMapPosition =
      isLiveMapPosition(areaMapPosition) &&
      (areaMapPosition !== initialAreaMapPositionRef.current?.value ||
        filtersChangedFromInitial ||
        currentPage !== page ||
        showBorder !== initialShowBorder);

    if (shouldUseLiveMapPosition && areaMapPosition?.bounds) {
      const n = normalizeBoundsForListingQuery(areaMapPosition.bounds);
      boundingBox = { minLat: n.southLat, maxLat: n.northLat, minLon: n.westLng, maxLon: n.eastLng };
    }

    let liveFilters: HotpadsWebListingFilter;
    if (effectiveFilterState) {
      liveFilters = buildSrpFilters(effectiveFilterState, boundingBox, clipScope);
    } else {
      liveFilters = {
        ...initialParams.filters,
        ...(boundingBox ? { boundingBox } : {}),
      };
      if (clipScope) {
        liveFilters.scope = clipScope;
      } else {
        delete liveFilters.scope;
      }
    }

    // Reuse the SSR request reference when nothing changed so the seeded query
    // doesn't refetch on mount. The filter compare is stringify-based, which is
    // safe only because this and the server build filters via the same
    // deterministic filterStateToV3Filter (stable key + array order).
    const unchanged =
      currentPage === page &&
      anchorSlug === initialParams.searchSlug &&
      JSON.stringify(liveFilters) === JSON.stringify(initialParams.filters ?? {});
    if (unchanged) return initialParams;

    return {
      ...initialParams,
      includeBreadcrumbs: false,
      searchSlug: anchorSlug,
      filters: liveFilters,
      page: { number: currentPage, limit: LISTINGS_PER_PAGE },
    };
  }, [
    initialParams,
    areaMapPosition,
    filters,
    queryFilters,
    hasInteracted,
    allRentalsSlug,
    currentPage,
    page,
    borderState,
    areaKey,
    scope,
  ]);
}
